import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";

import {
  MapPin,
  LogIn,
  User,
  ChevronDown,
  ShoppingCart,
} from "lucide-react";

import api from "../../api";
import logo from "../../Imgs/IMG_5290.jpg";

function Navbar({
  selectedArea,
  setSelectedArea,
  isLoggedIn,
  setIsLoggedIn,
  onLoginClick,
}) {
  const [areas, setAreas] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const filteredAreas = areas.filter((area) =>
    area.name.toLowerCase().includes(selectedArea.toLowerCase())
  );

  // ============================================================
  // Load selectedArea from localStorage
  // ============================================================

  useEffect(() => {
    const savedArea = localStorage.getItem("selectedArea");
    if (savedArea) {
      setSelectedArea(savedArea);
    }
  }, []);

  useEffect(() => {
    if (selectedArea) {
      localStorage.setItem("selectedArea", selectedArea);
    }
  }, [selectedArea]);

  // ============================================================
  // Fetch Areas
  // ============================================================

  useEffect(() => {
    const fetchAreas = async () => {
      try {
        const res = await api.get(
          "https://api.gulf.deals/master-catalogue/api/community-areas/dropdown",
          { headers: { Accept: "application/json" } }
        );
        setAreas(res.data || []);
      } catch (error) {
        console.error("Error fetching areas:", error);
      }
    };

    fetchAreas();
  }, []);

  // ============================================================
  // Near Me – Location & Reverse Geocoding
  // ============================================================

  useEffect(() => {
    if (selectedArea !== "Near me") return;

    const successHandler = async (pos) => {
      const lat = pos.coords.latitude;
      const lng = pos.coords.longitude;

      localStorage.setItem("latitude", lat);
      localStorage.setItem("longitude", lng);

      try {
        const res = await api.post(
          "/gulf-deals-auth/reverse-geocoding/v1",
          {
            latitude: lat,
            longitude: lng,
          }
        );

        const area = res.data?.area;
        if (area) setSelectedArea(area);
      } catch (err) {
        console.error("Reverse geocoding failed:", err);
      }
    };

    const errorHandler = (err) => {
      console.warn("❌ Location error:", err);
    };

    navigator.permissions
      ?.query({ name: "geolocation" })
      .then((result) => {
        if (result.state === "granted" || result.state === "prompt") {
          navigator.geolocation.getCurrentPosition(
            successHandler,
            errorHandler
          );
        }

        if (result.state === "denied") {
          alert(
            "⚠️ Location permission is blocked. Please enable it from browser settings."
          );
        }
      })
      .catch(() => {
        navigator.geolocation.getCurrentPosition(
          successHandler,
          errorHandler
        );
      });
  }, [selectedArea, setSelectedArea]);

  // ============================================================
  // Logout
  // ============================================================

  const handleLogout = async () => {
    try {
      const refreshToken = localStorage.getItem("refreshToken");

      if (!refreshToken) {
        setIsLoggedIn(false);
        return;
      }

      await api.post(
        "/gulf-deals-auth/api/auth/logout?refreshToken=" + refreshToken
      );

      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");

      setIsLoggedIn(false);
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  // ============================================================
  // Area Select (🔥 MAIN FIX)
  // ============================================================

  const selectArea = (area) => {
    setSelectedArea(area.name);

    if (area.latitude && area.longitude) {
      localStorage.setItem("latitude", area.latitude);
      localStorage.setItem("longitude", area.longitude);
    } else {
      localStorage.removeItem("latitude");
      localStorage.removeItem("longitude");
    }

    setIsOpen(false);
  };

  // ============================================================
  // Navbar UI (unchanged)
  // ============================================================

  return (
    <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-slate-200/70 px-4 py-2.5 flex flex-wrap items-center gap-3">
      {/* LOGO */}
      <div
        className="flex items-center"
        onClick={() => navigate("/")}
        style={{ cursor: "pointer" }}
      >
        <img
          src={logo}
          alt="Gulf.Deals Logo"
          className="md:w-[105px] md:h-[55px] w-[70px] h-[40px]"
        />
      </div>

      {/* AREA SEARCH */}
      <div className="flex-1 flex justify-center order-3 md:order-none">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-50 border border-indigo-100 text-xs text-slate-700">
          <MapPin className="w-3.5 h-3.5 text-indigo-600" />

          <div className="relative">
            <input
              type="text"
              value={selectedArea}
              onChange={(e) => {
                setSelectedArea(e.target.value);
                setIsOpen(true);
              }}
              onFocus={() => setIsOpen(true)}
              placeholder="Search area..."
              className="bg-transparent border-none focus:outline-none text-xs placeholder-slate-400"
            />

            {isOpen && (
              <div className="absolute top-full left-0 bg-white shadow-md rounded-md w-40 max-h-48 overflow-auto z-10">
                <div
                  className="px-2 py-1 hover:bg-slate-100 cursor-pointer"
                  onClick={() => {
                    setSelectedArea("Near me");
                    setIsOpen(false);
                  }}
                >
                  Near me
                </div>

                {filteredAreas.map((area) => (
                  <div
                    key={area.id}
                    className="px-2 py-1 hover:bg-slate-100 cursor-pointer"
                    onClick={() => selectArea(area)}
                  >
                    {area.name}
                  </div>
                ))}
              </div>
            )}
          </div>

          <ChevronDown
            className="w-3.5 h-3.5 text-slate-500"
            onClick={() => setIsOpen((prev) => !prev)}
          />

          {!localStorage.getItem("latitude") && (
            <div className="text-red-500 text-xs">Area not selected</div>
          )}
        </div>
      </div>

      {/* RIGHT SIDE */}
      <div className="flex items-center gap-2 ml-auto text-xs">
        <Link
          to="/Store"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-300 text-slate-700 hover:border-indigo-500 hover:text-indigo-600"
        >
          catalog
        </Link>

        <button
          type="button"
          onClick={() => {
            if (isLoggedIn) handleLogout();
            else onLoginClick();
          }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-indigo-600 bg-indigo-600 text-white shadow-md"
        >
          <LogIn className="w-3.5 h-3.5" />
          {isLoggedIn ? "Logout" : "Login"}
        </button>

        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 text-[0.72rem] text-slate-700">
          <User className="w-3.5 h-3.5" />
          <span>{isLoggedIn ? "Hi, User" : "Guest"}</span>
        </div>
      </div>
    </header>
  );
}

export default Navbar;
